// ./src/components/features/Inventory/InventoryItemsList/InventoryItemsList.tsx
//
// Inventory-items catalog list — table on desktop (>= md), cards on tablet/mobile.
// Columns: name (+ sku), category, on-hand total (+ unit), value, and a low-stock
// badge when the item is at/under its threshold. Tap-safe (explicit Edit/Delete
// only), rights-gated per row, selection-ready. Mirrors RentalAssetsList.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { InventoryItem } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment, useOptionalInventoryItemsContext, useOptionalCategoriesContext } from "@/contexts";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonLines, CardSkeletonCheckbox, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

export interface InventoryItemsListProps {
  data?: InventoryItem[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditItem?: (item: InventoryItem) => void;
  onDeleteItem?: (item: InventoryItem) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddItem?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search / filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so don't offer the first-run Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

const isDisabled = (item: InventoryItem): boolean => item.status === STATUSES.DISABLED;

const itemCurrency = (item: InventoryItem): string => item.currencyCode || "USD";

const StatusBadge: React.FC<{ item: InventoryItem }> = ({ item }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(item)) {
    return null;
  }

  return (
    <Badge size="sm" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const CategoryName: React.FC<{ item: InventoryItem }> = ({ item }) => {
  const categories = useOptionalCategoriesContext();
  const name = item.categoryId ? categories?.getById(item.categoryId)?.name : null;

  if (!name) {
    return null;
  }

  return (
    <Text size="sm" c="dimmed" lineClamp={1}>
      {name}
    </Text>
  );
};

const SkuCell: React.FC<{ item: InventoryItem }> = ({ item }) =>
  item.sku ? <Text size="md">{item.sku}</Text> : <Text size="md" c="dimmed">—</Text>;

const UnitCell: React.FC<{ item: InventoryItem }> = ({ item }) =>
  item.unit ? <Text size="md">{item.unit}</Text> : <Text size="md" c="dimmed">—</Text>;

const UnitCostCell: React.FC<{ item: InventoryItem }> = ({ item }) =>
  item.unitCost != null ? (
    <Text size="md">{formatCurrency(item.unitCost, itemCurrency(item))}</Text>
  ) : (
    <Text size="md" c="dimmed">—</Text>
  );

// The item's low-stock CONFIG (threshold + tracking), NOT the live stock state.
const LowStockConfigCell: React.FC<{ item: InventoryItem }> = ({ item }) => {
  const { t } = useTranslation(["inventory"]);

  if (item.trackLowStock && item.lowStockThreshold != null) {
    return (
      <Text size="md">
        {t("inventory:items.list.alertAt", { defaultValue: "Alert ≤ {{n}}", n: item.lowStockThreshold })}
      </Text>
    );
  }

  return (
    <Text size="md" c="dimmed">
      {t("inventory:items.list.alertOff", { defaultValue: "Off" })}
    </Text>
  );
};

const ItemIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="grape" radius="xl" size={size}>
    <AppIcon iconName="Package" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  item: InventoryItem;
  onEdit?: (i: InventoryItem) => void;
  onDelete?: (i: InventoryItem) => void;
  isDeleting: boolean;
}> = ({ item, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(item, "edit");
  const showDelete = !!onDelete && recordHasRight(item, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(item)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(item)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  item: InventoryItem;
  name: string;
  disabled: boolean;
  onEdit?: (i: InventoryItem) => void;
  onDelete?: (i: InventoryItem) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const ItemTableRow: React.FC<RowProps> = ({
  item,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => item.id && onToggleSelect?.(item.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <ItemIcon />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge item={item} />
            </Group>
            <CategoryName item={item} />
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 160 }}>
        <SkuCell item={item} />
      </Table.Td>
      <Table.Td style={{ width: 110 }}>
        <UnitCell item={item} />
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <UnitCostCell item={item} />
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <LowStockConfigCell item={item} />
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions item={item} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const ItemTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 110 }}>
      <Skeleton height={14} width={60} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={14} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={14} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ItemCardComponent: React.FC<CardProps> = ({
  item,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => item.id && onToggleSelect?.(item.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(item, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(item),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(item, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(item),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, item, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <ItemIcon size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge item={item} />
          </Group>
          <CategoryName item={item} />
          <Group gap="md" mt={6} wrap="wrap">
            {item.sku ? <Text size="md" c="dimmed">{item.sku}</Text> : null}
            <UnitCostCell item={item} />
            <LowStockConfigCell item={item} />
          </Group>
        </Box>
        {showActions && <RowActions item={item} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const ItemCard = React.memo(ItemCardComponent);

const ItemCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "sm", "md"]} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const InventoryItemsListComponent: React.FC<InventoryItemsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditItem,
  onDeleteItem,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddItem,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["inventory", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditItem, onDeleteItem);

  const itemsContext = useOptionalInventoryItemsContext();

  const items = dataProp ?? itemsContext?.inventoryItems ?? [];
  const loading = loadingProp ?? itemsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? itemsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? itemsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? itemsContext?.loadMore;
  const isRemoving = isRemovingProp ?? itemsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (item: InventoryItem) => {
      closeAllCards();
      onEditItem?.(item);
    },
    [onEditItem, closeAllCards]
  );

  const handleDelete = useCallback(
    (item: InventoryItem) => {
      closeAllCards();
      onDeleteItem?.(item);
    },
    [onDeleteItem, closeAllCards]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = itemsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && items.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("inventory:items.emptyState.noMatch", { defaultValue: "No inventory items match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("inventory:items.emptyState.title", { defaultValue: "No inventory items yet" })}
          description={t("inventory:items.emptyState.description", {
            defaultValue: "Build a catalog of stock-tracked supplies and gear to receive, count and consume across your events.",
          })}
          iconName="Package"
          actionLabel={t("inventory:items.emptyState.actionLabel", { defaultValue: "Add inventory item" })}
          onAction={onAddItem}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (item: InventoryItem): boolean =>
    isSelectable &&
    ((!!onEditItem && recordHasRight(item, "edit")) || (!!onDeleteItem && recordHasRight(item, "remove")));

  const rowProps = (item: InventoryItem): RowProps => ({
    item,
    name: item.name || "—",
    disabled: isDisabled(item),
    onEdit: onEditItem ? handleEdit : undefined,
    onDelete: onDeleteItem ? handleDelete : undefined,
    highlighted: item.id === highlightedId,
    isDeleting: isRemoving(item.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(item),
    selected: selection?.isSelected(item.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = items
    .filter((it) => rowIsSelectable(it))
    .map((it) => it.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("inventory:items.list.columns.name", { defaultValue: "Item" })}</Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t("inventory:items.list.columns.sku", { defaultValue: "SKU" })}
                </Table.Th>
                <Table.Th style={{ width: 110 }}>
                  {t("inventory:items.list.columns.unit", { defaultValue: "Unit" })}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>
                  {t("inventory:items.list.columns.unitCost", { defaultValue: "Unit cost" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }}>
                  {t("inventory:items.list.columns.lowStockConfig", { defaultValue: "Low-stock alert" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <ItemTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : items.map((item) => <ItemTableRow key={item.id} {...rowProps(item)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <ItemCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {items.map((item) => (
          <ItemCard
            key={item.id}
            {...rowProps(item)}
            forceClose={shouldForceClose(item.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(item.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(item.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const InventoryItemsList = React.memo(InventoryItemsListComponent);
